import { useEffect, useMemo } from "react";
import { useForm } from "react-hook-form";
import CommonModal from "../common11/commonModals/CommonModal";
import CommonCheckbox from "../common11/formInputs/CommonCheckbox";
import CommonDatePicker from "../common11/formInputs/CommonDatePicker";
import InputField from "../common11/formInputs/InputField";
import InputAreaField from "../common11/formInputs/InputAreaField";
import SearchDropdown from "../common11/formInputs/SearchDropdown";
import { showInfoToast } from "../common11/toastUtils";
import { MOCK_DOCTORS } from "../medicalRecord/medicalRecordMockData";

const today = () => new Date().toISOString().split("T")[0];

const CertificateFormModal = ({ isOpen, onClose, patientInfo = {} }) => {
  const { control, handleSubmit, reset, watch } = useForm({
    defaultValues: {
      certificateNo: "",
      title: "",
      treatment: false,
      treatmentType: null,
      treatmentText: "",
      treatmentFrom: today(),
      treatmentTo: today(),
      rest: false,
      restText: "He/she is advised rest for or Has been further advised rest",
      restDays: 10,
      operation: false,
      operationText: "is / was operated by me.",
      operationDate: today(),
      leave: false,
      leaveText: "Grant the leave as per the rule",
      admission: false,
      admissionText: "is / was admitted to the hospital",
      admissionDate: today(),
      fitness: false,
      fitnessText: "is fit to resume duty from Date",
      fitnessDate: today(),
      discharge: false,
      dischargeText: "is / was discharged on",
      dischargeDate: today(),
      emergency: false,
      emergencyText: "He/She was admitted as an Emergency.",
      signed: null,
      signedDate: today(),
    },
  });

  useEffect(() => {
    if (!isOpen) return;
    reset({
      certificateNo: "",
      title: "",
      treatment: false,
      treatmentType: null,
      treatmentText: "",
      treatmentFrom: today(),
      treatmentTo: today(),
      rest: false,
      restText: "He/she is advised rest for or Has been further advised rest",
      restDays: 10,
      operation: false,
      operationText: "is / was operated by me.",
      operationDate: today(),
      leave: false,
      leaveText: "Grant the leave as per the rule",
      admission: false,
      admissionText: "is / was admitted to the hospital",
      admissionDate: today(),
      fitness: false,
      fitnessText: "is fit to resume duty from Date",
      fitnessDate: today(),
      discharge: false,
      dischargeText: "is / was discharged on",
      dischargeDate: today(),
      emergency: false,
      emergencyText: "He/She was admitted as an Emergency.",
      signed: null,
      signedDate: today(),
    });
  }, [isOpen, reset]);

  const doctorOptions = useMemo(
    () =>
      MOCK_DOCTORS.map((d) => ({
        id: d.doctorId,
        label: d.doctorName,
        value: d.doctorId,
      })),
    []
  );

  const treatmentTypeOptions = [
    { id: "opd", label: "OPD", value: "opd" },
    { id: "ipd", label: "IPD", value: "ipd" },
    { id: "emergency", label: "Emergency", value: "emergency" },
  ];

  const handleSave = handleSubmit((data) => {
    showInfoToast("Certificate saved", { duration: 2000 });
    onClose();
  });

  const handleUpdate = handleSubmit((data) => {
    showInfoToast("Certificate updated", { duration: 2000 });
    onClose();
  });

  const handleNew = () => {
    reset({
      certificateNo: "",
      title: "",
      treatment: false,
      treatmentType: null,
      treatmentText: "",
      treatmentFrom: today(),
      treatmentTo: today(),
      rest: false,
      restText: "He/she is advised rest for or Has been further advised rest",
      restDays: 10,
      operation: false,
      operationText: "is / was operated by me.",
      operationDate: today(),
      leave: false,
      leaveText: "Grant the leave as per the rule",
      admission: false,
      admissionText: "is / was admitted to the hospital",
      admissionDate: today(),
      fitness: false,
      fitnessText: "is fit to resume duty from Date",
      fitnessDate: today(),
      discharge: false,
      dischargeText: "is / was discharged on",
      dischargeDate: today(),
      emergency: false,
      emergencyText: "He/She was admitted as an Emergency.",
      signed: watch("signed"),
      signedDate: today(),
    });
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  const treatmentChecked = watch("treatment");
  const restChecked = watch("rest");
  const operationChecked = watch("operation");
  const leaveChecked = watch("leave");
  const admissionChecked = watch("admission");
  const fitnessChecked = watch("fitness");
  const dischargeChecked = watch("discharge");
  const emergencyChecked = watch("emergency");

  const modalActions = [
    {
      label: "New",
      onClick: handleNew,
      variant: "outlined",
      type: "button",
      className: "!border-gray-300 !text-gray-700 hover:!bg-gray-50",
    },
    {
      label: "Save",
      onClick: handleSave,
      variant: "primary-filled",
      type: "button",
      className: "!bg-red-600 hover:!bg-red-700 !text-white",
    },
    {
      label: "Update",
      onClick: handleUpdate,
      variant: "primary-filled",
      type: "button",
      className: "!bg-red-600 hover:!bg-red-700 !text-white",
    },
    {
      label: "Close",
      onClick: handleClose,
      variant: "outlined",
      type: "button",
      className: "!border-gray-300 !text-gray-700 hover:!bg-gray-50",
    },
  ];

  if (!isOpen) return null;

  return (
    <CommonModal
      isOpen={isOpen}
      onClose={handleClose}
      title="Certificate"
      size="lg"
      showCloseButton
      disableBackdropClose
      actions={modalActions}
    >
      <div className="flex flex-col gap-4 max-h-[70vh] overflow-y-auto pr-1">
        {/* Certificate No. & Title */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <InputField
            name="certificateNo"
            control={control}
            label="Certificate No."
            placeholder="e.g. 48"
            fontSize="text-xs"
            isFloatingLabel={false}
          />
          <InputField
            name="title"
            control={control}
            label="Title"
            placeholder="Enter title"
            fontSize="text-xs"
            isFloatingLabel={false}
          />
        </div>

        {/* Treatment */}
        <div className="rounded border border-gray-200 p-3 space-y-2">
          <div className="flex flex-wrap items-center gap-2">
            <CommonCheckbox name="treatment" control={control} label="Treatment" fontSize="text-xs" />
            <div className="min-w-[140px]">
              <SearchDropdown
                name="treatmentType"
                control={control}
                label=""
                dataArray={treatmentTypeOptions}
                placeholder="<Select>"
                isSearchable
                isClearable
                fontSize="text-xs"
                isFloatingLabel={false}
              />
            </div>
          </div>
          {treatmentChecked && (
            <>
              <InputAreaField
                name="treatmentText"
                control={control}
                label=""
                placeholder="Treatment details..."
                fontSize="text-xs"
                minRows={2}
                isFloatingLabel={false}
              />
              <p className="text-xs text-gray-600">is / was treated by me.</p>
              <div className="grid grid-cols-2 gap-3">
                <CommonDatePicker name="treatmentFrom" control={control} label="From" fontSize="text-xs" isFloatingLabel={false} />
                <CommonDatePicker name="treatmentTo" control={control} label="To" fontSize="text-xs" isFloatingLabel={false} />
              </div>
            </>
          )}
        </div>

        {/* Rest */}
        <div className="flex flex-wrap items-center gap-3">
          <CommonCheckbox name="rest" control={control} label="Rest" fontSize="text-xs" />
          {restChecked && (
            <>
              <InputField name="restText" control={control} label="" placeholder="Rest advice" fontSize="text-xs" isFloatingLabel={false} className="flex-1 min-w-[200px]" />
              <div className="flex items-center gap-2">
                <InputField name="restDays" control={control} label="No. of Days" type="number" fontSize="text-xs" isFloatingLabel={false} className="w-20" />
              </div>
            </>
          )}
        </div>

        {/* Operation */}
        <div className="flex flex-wrap items-center gap-3">
          <CommonCheckbox name="operation" control={control} label="Operation" fontSize="text-xs" />
          {operationChecked && (
            <>
              <span className="text-xs text-gray-600">{watch("operationText")}</span>
              <CommonDatePicker name="operationDate" control={control} label="" fontSize="text-xs" isFloatingLabel={false} />
            </>
          )}
        </div>

        {/* Leave */}
        <div className="flex flex-wrap items-center gap-3">
          <CommonCheckbox name="leave" control={control} label="Leave" fontSize="text-xs" />
          {leaveChecked && (
            <InputField name="leaveText" control={control} label="" placeholder="Leave text" fontSize="text-xs" isFloatingLabel={false} className="flex-1 min-w-[200px]" />
          )}
        </div>

        {/* Admission */}
        <div className="flex flex-wrap items-center gap-3">
          <CommonCheckbox name="admission" control={control} label="Admission" fontSize="text-xs" />
          {admissionChecked && (
            <>
              <span className="text-xs text-gray-600">{watch("admissionText")}</span>
              <CommonDatePicker name="admissionDate" control={control} label="" fontSize="text-xs" isFloatingLabel={false} />
            </>
          )}
        </div>

        {/* Fitness */}
        <div className="flex flex-wrap items-center gap-3">
          <CommonCheckbox name="fitness" control={control} label="Fitness" fontSize="text-xs" />
          {fitnessChecked && (
            <>
              <span className="text-xs text-gray-600">{watch("fitnessText")}</span>
              <CommonDatePicker name="fitnessDate" control={control} label="" fontSize="text-xs" isFloatingLabel={false} />
            </>
          )}
        </div>

        {/* Discharge */}
        <div className="flex flex-wrap items-center gap-3">
          <CommonCheckbox name="discharge" control={control} label="Discharge" fontSize="text-xs" />
          {dischargeChecked && (
            <>
              <span className="text-xs text-gray-600">{watch("dischargeText")}</span>
              <CommonDatePicker name="dischargeDate" control={control} label="" fontSize="text-xs" isFloatingLabel={false} />
            </>
          )}
        </div>

        {/* Emergency */}
        <div className="flex flex-wrap items-center gap-3">
          <CommonCheckbox name="emergency" control={control} label="Emergency" fontSize="text-xs" />
          {emergencyChecked && <span className="text-xs text-gray-600">{watch("emergencyText")}</span>}
        </div>

        {/* Signed & Date */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-2 border-t border-gray-200">
          <SearchDropdown
            name="signed"
            control={control}
            label="Signed"
            dataArray={doctorOptions}
            placeholder="Select signatory"
            isSearchable
            isClearable
            fontSize="text-xs"
            isFloatingLabel={false}
          />
          <CommonDatePicker name="signedDate" control={control} label="Date" fontSize="text-xs" isFloatingLabel={false} />
        </div>
      </div>
    </CommonModal>
  );
};

export default CertificateFormModal;
